import type { JSX } from 'react';
import { FormField } from '@/components/form';
import { PROPERTY_CODE_PATTERN } from '@/lib/constants/validationPatterns';
import {
  ASSET_TYPE_LABELS,
  ASSET_TYPE_OPTIONS,
  SUBMARKET_OPTIONS,
} from '@/lib/constants/propertyOptions';
import type {
  PropertyCreateFieldChangeFactory,
  PropertyCreateFormErrors,
  PropertyCreateFormState,
} from './types';

interface PropertyCreateBasicsSectionProps {
  formState: PropertyCreateFormState;
  errors: PropertyCreateFormErrors;
  disabled: boolean;
  inputClass: string;
  onFieldChange: PropertyCreateFieldChangeFactory;
}

export function PropertyCreateBasicsSection({
  formState,
  errors,
  disabled,
  inputClass,
  onFieldChange,
}: PropertyCreateBasicsSectionProps): JSX.Element {
  return (
    <>
      <section className="grid gap-4 md:grid-cols-2">
        <FormField
          id="property-code"
          label="Property code"
          description="Short identifier used in URLs and public listings. Letters/numbers plus - or _. Example: 501_ALLIANCE."
          required
          error={errors.propertyCode}
        >
          <input
            value={formState.propertyCode}
            onChange={onFieldChange('propertyCode')}
            minLength={3}
            maxLength={64}
            pattern={PROPERTY_CODE_PATTERN}
            className={inputClass}
            disabled={disabled}
          />
        </FormField>
        <FormField
          id="submarket"
          label="Submarket"
          description="Used for public filters and portfolio reporting."
          required
          error={errors.submarket}
        >
          <select
            value={formState.submarket}
            onChange={onFieldChange('submarket')}
            className={inputClass}
            disabled={disabled}
          >
            <option value="" disabled>
              Select submarket
            </option>
            {SUBMARKET_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </FormField>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <FormField
          id="asset-type"
          label="Asset type"
          description="Controls public labels and leasing prompts."
          required
          error={errors.assetType}
        >
          <select
            value={formState.assetType}
            onChange={onFieldChange('assetType')}
            className={inputClass}
            disabled={disabled}
          >
            <option value="" disabled>
              Select asset type
            </option>
            {ASSET_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {ASSET_TYPE_LABELS[option]}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          id="total-rentable-sf"
          label="Total rentable SF"
          description="Total building size shown on public property pages. Example: 125000."
          required
          error={errors.totalRentableSf}
        >
          <input
            type="number"
            inputMode="decimal"
            min={1}
            value={formState.totalRentableSf}
            onChange={onFieldChange('totalRentableSf')}
            className={inputClass}
            disabled={disabled}
          />
        </FormField>
      </section>

      <FormField
        id="marketing-summary"
        label="Marketing summary"
        description="Appears in the public property hero and listing cards. Aim for 2-3 sentences."
        required
        error={errors.marketingSummary}
      >
        <textarea
          value={formState.marketingSummary}
          onChange={onFieldChange('marketingSummary')}
          minLength={20}
          maxLength={2000}
          className={inputClass}
          rows={6}
          disabled={disabled}
        />
      </FormField>
    </>
  );
}
